import React, { useState } from 'react';
import { 
  BookOpen, ExternalLink, FileText, Award, Users, TrendingUp,
  GraduationCap, BarChart3, ChevronDown, ChevronRight, Search,
  Building2, Brain, Target, Lightbulb
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Research and evidence data
const RESEARCH_CATEGORIES = [
  {
    id: 'evaluation-frameworks',
    name: 'Learning Evaluation Frameworks',
    icon: Target,
    color: 'blue',
    description: 'Foundational models for measuring learning effectiveness and business impact',
    sources: [
      {
        name: 'Kirkpatrick Model',
        author: 'Donald & James Kirkpatrick',
        organization: 'Kirkpatrick Partners',
        description: 'The four-level training evaluation model (Reaction, Learning, Behavior, Results) that forms the foundation for measuring training effectiveness.',
        url: 'https://www.kirkpatrickpartners.com/the-kirkpatrick-model/',
        type: 'Framework',
        relevance: 'Our Level 3 (Behavior) and Level 4 (Results) measurements align with Kirkpatrick\'s proven methodology for connecting learning to business outcomes.'
      },
      {
        name: 'Phillips ROI Methodology',
        author: 'Jack J. Phillips, PhD',
        organization: 'ROI Institute',
        description: 'Extended Kirkpatrick model to include Level 5 (ROI), providing a systematic approach to calculating the financial return on learning investments.',
        url: 'https://roiinstitute.net/',
        type: 'Framework',
        relevance: 'Our ROI calculations follow Phillips\' methodology for isolating learning impact and converting to monetary value.'
      },
      {
        name: 'Brinkerhoff Success Case Method',
        author: 'Robert O. Brinkerhoff',
        organization: 'The Learning Alliance',
        description: 'Evidence-based approach focusing on identifying what works and what doesn\'t in training programs through success case analysis.',
        url: 'https://www.wiley.com/en-us/The+Success+Case+Method',
        type: 'Methodology',
        relevance: 'Informs our approach to identifying high-impact learning interventions and understanding transfer factors.'
      }
    ]
  },
  {
    id: 'capability-transfer',
    name: 'Capability Transfer Research',
    icon: Brain,
    color: 'purple',
    description: 'Research on how learning translates to workplace capability and performance',
    sources: [
      {
        name: 'Transfer of Training Research',
        author: 'Baldwin & Ford',
        organization: 'Journal of Management',
        description: 'Seminal research on training transfer factors including trainee characteristics, training design, and work environment.',
        url: 'https://journals.sagepub.com/doi/10.1177/014920638801400102',
        type: 'Academic Research',
        relevance: 'Our Capability Transfer Rate model incorporates these validated transfer factors.'
      },
      {
        name: '70-20-10 Learning Model',
        author: 'Morgan McCall, Michael Lombardo, Robert Eichinger',
        organization: 'Center for Creative Leadership',
        description: 'Research-based model showing 70% of learning happens through experience, 20% through others, and 10% through formal training.',
        url: 'https://www.ccl.org/',
        type: 'Research Model',
        relevance: 'Informs our blended learning modality effectiveness calculations and on-the-job application assumptions.'
      },
      {
        name: 'Forgetting Curve & Spaced Learning',
        author: 'Hermann Ebbinghaus',
        organization: 'Memory Research',
        description: 'Foundational research on memory retention decay and the effectiveness of spaced repetition in learning.',
        url: 'https://www.learning-theories.com/forgetting-curve-ebbinghaus.html',
        type: 'Cognitive Science',
        relevance: 'Our time decay factors and microlearning effectiveness multipliers are based on this research.'
      }
    ]
  },
  {
    id: 'industry-benchmarks',
    name: 'Industry Benchmarks & Research',
    icon: BarChart3,
    color: 'teal',
    description: 'Industry data and benchmarks for L&D effectiveness and investment',
    sources: [
      {
        name: 'ATD State of the Industry',
        author: 'Association for Talent Development',
        organization: 'ATD Research',
        description: 'Annual comprehensive research on L&D spending, practices, and outcomes across industries.',
        url: 'https://www.td.org/research-reports',
        type: 'Industry Report',
        relevance: 'Provides baseline data for training costs, delivery methods, and industry benchmarks used in our calculations.'
      },
      {
        name: 'Brandon Hall Group Research',
        author: 'Brandon Hall Group',
        organization: 'Brandon Hall Group',
        description: 'Research on learning technology, leadership development, and talent management best practices.',
        url: 'https://www.brandonhall.com/research/',
        type: 'Industry Research',
        relevance: 'Informs our modality effectiveness factors and technology-enabled learning multipliers.'
      },
      {
        name: 'Training Industry Reports',
        author: 'Training Industry Inc.',
        organization: 'Training Industry',
        description: 'Research on training market trends, provider capabilities, and learning effectiveness.',
        url: 'https://trainingindustry.com/research/',
        type: 'Market Research',
        relevance: 'Provides intervention cost benchmarks and delivery modality comparisons.'
      },
      {
        name: 'Bersin Research (Deloitte)',
        author: 'Josh Bersin',
        organization: 'Deloitte / The Josh Bersin Company',
        description: 'Research on HR technology, learning experience platforms, and workforce development trends.',
        url: 'https://joshbersin.com/research/',
        type: 'Industry Analysis',
        relevance: 'Informs our microlearning and digital learning effectiveness factors.'
      }
    ]
  },
  {
    id: 'business-impact',
    name: 'Business Impact Studies',
    icon: TrendingUp,
    color: 'emerald',
    description: 'Research linking learning investments to measurable business outcomes',
    sources: [
      {
        name: 'McKinsey L&D Research',
        author: 'McKinsey & Company',
        organization: 'McKinsey',
        description: 'Research on capability building, reskilling, and the business impact of learning programs.',
        url: 'https://www.mckinsey.com/capabilities/people-and-organizational-performance/our-insights',
        type: 'Consulting Research',
        relevance: 'Provides evidence for leadership development impact and capability-to-KPI relationships.'
      },
      {
        name: 'CIPD Learning Research',
        author: 'Chartered Institute of Personnel and Development',
        organization: 'CIPD',
        description: 'UK-based research on learning effectiveness, employee development, and organizational impact.',
        url: 'https://www.cipd.org/uk/knowledge/reports/',
        type: 'Professional Body Research',
        relevance: 'Informs our elasticity factors and industry-specific impact modifiers.'
      },
      {
        name: 'Harvard Business Review Studies',
        author: 'Various Academic Authors',
        organization: 'Harvard Business Publishing',
        description: 'Peer-reviewed research on management practices, leadership development, and organizational effectiveness.',
        url: 'https://hbr.org/topic/subject/employee-development',
        type: 'Academic/Practitioner Research',
        relevance: 'Provides evidence base for intervention-to-KPI alignment scores.'
      }
    ]
  },
  {
    id: 'leadership-development',
    name: 'Leadership Development Research',
    icon: Users,
    color: 'amber',
    description: 'Evidence on leadership training effectiveness and engagement impact',
    sources: [
      {
        name: 'Gallup Manager Research',
        author: 'Gallup',
        organization: 'Gallup',
        description: 'Research showing that managers account for 70% of variance in employee engagement.',
        url: 'https://www.gallup.com/workplace/insights.aspx',
        type: 'Engagement Research',
        relevance: 'Validates our high alignment scores between leadership development and engagement/retention KPIs.'
      },
      {
        name: 'DDI Global Leadership Forecast',
        author: 'Development Dimensions International',
        organization: 'DDI',
        description: 'Longitudinal research on leadership development practices and organizational performance.',
        url: 'https://www.ddiworld.com/research',
        type: 'Leadership Research',
        relevance: 'Provides benchmarks for leadership development effectiveness and readiness metrics.'
      },
      {
        name: 'Center for Creative Leadership Research',
        author: 'CCL',
        organization: 'Center for Creative Leadership',
        description: 'Research on leader development, leadership effectiveness, and organizational culture.',
        url: 'https://www.ccl.org/articles/',
        type: 'Academic Research',
        relevance: 'Informs our leadership intervention design and impact assumptions.'
      }
    ]
  },
  {
    id: 'learning-science',
    name: 'Learning Science & Psychology',
    icon: Lightbulb,
    color: 'rose',
    description: 'Cognitive science and psychology research underlying effective learning design',
    sources: [
      {
        name: 'Journal of Applied Psychology',
        author: 'American Psychological Association',
        organization: 'APA',
        description: 'Peer-reviewed research on training effectiveness, skill retention, and workplace learning.',
        url: 'https://www.apa.org/pubs/journals/apl',
        type: 'Academic Journal',
        relevance: 'Source of meta-analyses on training effectiveness that inform our modality multipliers.'
      },
      {
        name: 'Learning & Instruction Journal',
        author: 'Elsevier',
        organization: 'European Association for Research on Learning and Instruction',
        description: 'Research on instructional design, educational psychology, and learning outcomes.',
        url: 'https://www.sciencedirect.com/journal/learning-and-instruction',
        type: 'Academic Journal',
        relevance: 'Provides evidence for learning design principles that impact transfer rates.'
      },
      {
        name: 'Make It Stick: The Science of Successful Learning',
        author: 'Brown, Roediger, McDaniel',
        organization: 'Harvard University Press',
        description: 'Research-based book on effective learning strategies and retention science.',
        url: 'https://www.hup.harvard.edu/catalog.php?isbn=9780674729018',
        type: 'Book',
        relevance: 'Informs our understanding of spaced learning and retrieval practice effectiveness.'
      }
    ]
  }
];

const ResearchEvidencePage = () => {
  const [expandedCategories, setExpandedCategories] = useState(['evaluation-frameworks']);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const expandAll = () => {
    setExpandedCategories(RESEARCH_CATEGORIES.map(c => c.id));
  };

  const collapseAll = () => {
    setExpandedCategories([]);
  };

  const filteredCategories = RESEARCH_CATEGORIES.map(category => ({
    ...category,
    sources: category.sources.filter(source => 
      searchQuery === '' ||
      source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.sources.length > 0 || searchQuery === '');

  const totalSources = RESEARCH_CATEGORIES.reduce((acc, cat) => acc + cat.sources.length, 0);

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    teal: 'bg-teal-100 text-teal-700 border-teal-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    rose: 'bg-rose-100 text-rose-700 border-rose-200',
  };

  const __pageTheme = getLightSectionTheme('Navy');
  const researchHeroActions = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={expandAll}
        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="research-expand-all"
      >
        Expand All
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={collapseAll}
        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="research-collapse-all"
      >
        Collapse All
      </Button>
    </>
  );
  const researchHeroStats = [
    { label: 'Research Categories', value: RESEARCH_CATEGORIES.length, testId: 'research-stat-categories' },
    { label: 'Source Documents', value: totalSources, testId: 'research-stat-sources' },
    { label: 'Frameworks', value: 3, testId: 'research-stat-frameworks' },
    { label: 'Industry Bodies', value: '8+', testId: 'research-stat-industry' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Research & Evidence"
        description="Our methodology is grounded in peer-reviewed academic research, industry benchmarks, and established evaluation frameworks. Explore the evidence base that powers our calculations."
        theme={__pageTheme}
        icon={Award}
        actions={researchHeroActions}
        stats={researchHeroStats}
        testId="research-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6">

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search research sources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Key Frameworks Highlight */}
      <Card className="bg-gradient-to-r from-navy-50 to-slate-50 border-navy-200">
        <CardContent className="py-6">
          <h3 className="font-bold text-navy-800 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Core Evaluation Frameworks We Build Upon
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="font-semibold text-slate-800">Kirkpatrick Model</p>
              <p className="text-sm text-slate-600 mt-1">4-level evaluation framework for measuring training impact</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="font-semibold text-slate-800">Phillips ROI</p>
              <p className="text-sm text-slate-600 mt-1">Extends to Level 5 with financial ROI calculations</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <p className="font-semibold text-slate-800">Success Case Method</p>
              <p className="text-sm text-slate-600 mt-1">Evidence-based approach to identify what works</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Research Categories */}
      <div className="space-y-4">
        {filteredCategories.map((category) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.includes(category.id);
          
          return (
            <Card key={category.id} className="overflow-hidden">
              <CardHeader 
                className={`cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'border-b' : ''}`}
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colorClasses[category.color]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{category.name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-0.5">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">{category.sources.length} sources</span>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {category.sources.map((source, idx) => (
                      <div key={source.title || `source-${idx}`} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-800">{source.name}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${colorClasses[category.color]}`}>
                                {source.type}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              <span className="font-medium">{source.author}</span>
                              {source.organization !== source.author && (
                                <> · {source.organization}</>
                              )}
                            </p>
                            <p className="text-sm text-slate-700 mb-3">{source.description}</p>
                            <div className="bg-white rounded p-3 border border-slate-100">
                              <p className="text-xs text-slate-500 uppercase font-medium mb-1">How We Use This</p>
                              <p className="text-sm text-slate-700">{source.relevance}</p>
                            </div>
                          </div>
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                          >
                            Visit
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
        <CardContent className="py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-6 w-6 text-teal-600" />
              <div>
                <p className="font-semibold text-slate-800">Want to learn more about our LEI measures?</p>
                <p className="text-sm text-slate-600">Explore all 15 measures across 5 pillars with formulas and benchmarks.</p>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = '/lei-reference'}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              View LEI Measures Guide
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
};

export default ResearchEvidencePage;
